import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  AlertCircle,
  Plus,
  ArrowRight
} from "lucide-react";

import DashboardStats from "../components/dashboard/DashboardStats";
import QuickActions from "../components/dashboard/QuickActions";
import TodaySchedule from "../components/dashboard/TodaySchedule";
import RecentVisits from "../components/dashboard/RecentVisits";

// Simple components to replace Base44 imports
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="px-6 py-4 border-b">
    {children}
  </div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, className = "", variant = "default", ...props }) => {
  const baseClasses = "px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center justify-center";
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50"
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Simple utility functions to replace Base44 utilities
const createPageUrl = (page) => `/${page.toLowerCase()}`;

const format = (date, formatStr) => {
  const options = {
    'EEEE, MMMM d, yyyy': {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    },
    'yyyy-MM-dd': {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }
  };

  if (formatStr === 'yyyy-MM-dd') {
    return date.toISOString().split('T')[0];
  }

  return new Intl.DateTimeFormat('en-US', options[formatStr] || {}).format(date);
};

const isThisWeek = (date) => {
  const today = new Date();
  const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return date >= startOfWeek && date <= endOfWeek;
};

// Mock entities to replace Base44 entities
const mockUser = { full_name: "Shriyam Singh", territory: "North Zone" };
const mockDoctors = Array.from({ length: 22 }, (_, i) => ({ id: i + 1, name: `Doctor ${i + 1}` }));
const mockVisits = [];
const mockExpenses = [{ id: 1, status: 'Pending', amount: 500 }];

const User = {
  me: async () => mockUser
};

const Doctor = {
  list: async () => mockDoctors
};

const Visit = {
  list: async (sort, limit) => mockVisits
};

const Expense = {
  filter: async (query) => mockExpenses.filter(e => e.status === query.status)
};

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    totalDoctors: 0,
    visitsToday: 0,
    visitsThisWeek: 0,
    pendingExpenses: 0
  });
  const [todayVisits, setTodayVisits] = useState([]);
  const [recentVisits, setRecentVisits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const userData = await User.me();
      setUser(userData);

      const [doctors, visits, expenses] = await Promise.all([
        Doctor.list(),
        Visit.list('-visit_date', 50),
        Expense.filter({ status: 'Pending' })
      ]);

      const today = new Date();
      const todayString = format(today, 'yyyy-MM-dd');

      const visitsToday = visits.filter(v => v.visit_date === todayString);
      const visitsThisWeek = visits.filter(v => isThisWeek(new Date(v.visit_date)));

      setStats({
        totalDoctors: doctors.length,
        visitsToday: visitsToday.length,
        visitsThisWeek: visitsThisWeek.length,
        pendingExpenses: expenses.length
      });

      setTodayVisits(visitsToday);
      setRecentVisits(visits.slice(0, 5));
    } catch (error) {
      console.error("Error loading dashboard:", error);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[1,2,3,4].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-16 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-blue-50 via-white to-indigo-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome back, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-blue-600 mt-1">
              {format(new Date(), 'EEEE, MMMM d, yyyy')} • {user?.territory}
            </p>
          </div>
          <div className="flex gap-3">
            <Link to={createPageUrl("Visits")}>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Plan Visit
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <DashboardStats stats={stats} />

        {/* Quick Actions */}
        <QuickActions />

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <TodaySchedule visits={todayVisits} />
            <RecentVisits visits={recentVisits} />
          </div>

          <div className="space-y-6">
            {/* Territory Overview */}
            <Card className="shadow-lg border-0 bg-gradient-to-br from-white to-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Territory Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Doctors</span>
                    <span className="font-bold text-xl">{stats.totalDoctors}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">This Week</span>
                    <span className="font-bold text-xl text-green-600">{stats.visitsThisWeek}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Coverage</span>
                    <span className="font-bold text-xl text-blue-600">
                      {stats.totalDoctors > 0 ? Math.round((stats.visitsThisWeek / stats.totalDoctors) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Items */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Pending Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                    <span className="text-amber-700">Expense Claims</span>
                    <span className="font-bold text-amber-700">{stats.pendingExpenses}</span>
                  </div>
                  <Link to={createPageUrl("Expenses")}>
                    <Button variant="outline" className="w-full justify-between">
                      Review Expenses
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
